import React, { useEffect, useState } from 'react';

import { useBreakpoint } from '@ast/magma/breakpoints';

import Trash from '@ast/magma/components/icon/icons/Trash';

import { Button } from '@ast/magma/components/button';
import { Dialog, DialogProps, DialogButtonType } from '@ast/magma/components/dialog';

import { ContactType, Exact, Scalars } from '@app/queryTyping';

import { Alert as AlertType, useNotifierContext } from '@app/common/components/Notifier';
import { OverlaySpinner } from '@app/common/components/overlayspinner';

import {
  isTriggeredEventCanceledApolloError,
} from '@app/core/components/TriggeredEvents/TriggeredEventCanceledApolloError';

import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import {
  useRemoveUserContactMutation,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/removeUserContact';

import { useRemoveUserAddressMutation } from '../../queries/queryTyping/removeUserAddress';

import styles from './DeleteModal.pcss';

export enum AddressType { Address = 'Address'}
export type DeleteContactType = ContactType | AddressType;

export type RemoveUserContact = Exact<{
  contactId: Scalars['ID'];
  contactType: DeleteContactType;
  contactEnrolled?: boolean;
}>;

export interface DeleteModalProps {
  readonly dialogOptions: DialogProps;
  readonly loading?: boolean;
  readonly contact: RemoveUserContact;
  readonly successMessage?: string;
}

export const DeleteModal: React.FC<DeleteModalProps> = ({
  children,
  dialogOptions,
  contact,
  successMessage,
}) => {
  const { t } = useUserSettingsTranslation();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { settingsQuery } = useSettingFieldsContext();
  const { setQueue, addAlerts, clearAlerts } = useNotifierContext();
  const [deleteContact, { error: contactError }] = useRemoveUserContactMutation();
  const [deleteAddress, { error: addressError }] = useRemoveUserAddressMutation();
  const isMobile = useBreakpoint().device === 'mobile';
  const error = contactError || addressError;

  const openDialog = () => {
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleUpdate = () => {
    // TODO: Handle generic errors from utils
    if (successMessage) {
      setQueue([{
        level: 'success',
        title: successMessage,
      }] as AlertType[]);
    }
    settingsQuery.refetch();
  };

  const handleDelete = async () => {
    setLoading(true);
    if (contact.contactType === AddressType.Address) {
      await deleteAddress({
        variables: {
          addressId: contact.contactId,
        },
        update(_, { data }) {
          if (data?.removeUserAddress?.errors?.length
              && data.removeUserAddress.errors[0].messages?.length) {
            clearAlerts();
            addAlerts([{
              level: 'error',
              title: data.removeUserAddress.errors[0].messages[0],
            }] as AlertType[]);
            setLoading(false);
            closeDialog();
            settingsQuery.refetch();
          } else {
            handleUpdate();
          }
        },
      });
    } else {
      await deleteContact({
        variables: {
          contactId: contact.contactId,
          contactType: contact.contactType as ContactType,
        },
        update: handleUpdate,
      });
    }
  };

  useEffect(() => {
    if (error && isTriggeredEventCanceledApolloError(error)) {
      setLoading(false);
    }
  }, [error]);

  if (error && !isTriggeredEventCanceledApolloError(error)) {
    throw error;
  }

  return (
    <div data-stable-name="DeleteDialogWrapper">
      <Button
        data-stable-name="DeleteActionButton"
        disabled={contact?.contactEnrolled}
        look="icon"
        icon={<Trash />}
        onClick={openDialog}
        aria-label={t(
          'delete-modal.action-button.aria-label|Accessibility text for delete modal action button',
          'Open delete modal',
        )}
      />
      {isOpen && (
        <Dialog
          predefinedWidth="md"
          align={isMobile ? 'bottom' : 'center'}
          className={styles.dialogWrapper}
          defaultButton={DialogButtonType.OK}
          fixedTitle={false}
          onClose={closeDialog}
          showClose={{
            // eslint-disable-next-line i18next/no-literal-string
            'aria-label': t(
              'delete-modal.close-button.aria-label|Accessibility text for close button on delete contacts dialog',
              'Close delete modal',
            ),
          }}
          buttons={[{
            type: DialogButtonType.Cancel,
            label: t('delete-modal.cancel-button|Delete contacts dialog cancel button', 'Cancel'),
          }, {
            type: DialogButtonType.OK,
            label: t('delete-modal.delete-button|Delete contacts dialog delete button label', 'Delete'),
            onClick: async () => {
              await handleDelete();
              return true;
            },
          }]}
          {...dialogOptions}
        >
          <>
            <div className={styles.messageWrapper}>
              {children}
            </div>
            <OverlaySpinner visible={loading} />
          </>
        </Dialog>
      )}
    </div>
  );
};
